// ✅ src/pages/VehicleV2/DieselConsumptionReport.jsx
// UPDATED — Production listing template
// 3-column filters • overflow locked • important grid columns

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaPrint,
  FaArrowLeft,
  FaInfoCircle,
  FaFileExport,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/dieselConsumption";

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const fmt2 = (v) =>
  toNum(v, 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const exportToCsv = (rows, filename = "diesel-consumption.csv") => {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => `"${String(r?.[h] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const createDefaultFilters = (branchType, branchId) => ({
  vehtype: "O",
  branch: branchType === "HO" ? "Select" : toStr(branchId),
  vehRegNo: "",
  month: "Select",
  year: "Select",
  fromDate: "",
  toDate: "",
  reportType: "Select",
  vehicleMainType: "Select",
  vehicleName: "",
  vehicleCode: "",
});

export default function DieselConsumptionReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toNum(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toNum(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  const didInitRef = useRef(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const isBusy = pageLoading || dropdownLoading || listLoading;
  const [showFilters, setShowFilters] = useState(true);
  const [branches, setBranches] = useState([{ label: "Select", value: "Select" }]);
  const [years, setYears] = useState([{ label: "Select", value: "Select" }]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([{ label: "Select", value: "Select" }]);
  const [logCtx, setLogCtx] = useState({
    logvehicleid: "",
    logvehiclecode: "",
    logvehicledetid: "",
    logvehiclebranchid: "",
    logvehicleregno: "",
  });
  const [vehRegSuggestions, setVehRegSuggestions] = useState([]);
  const [showVehRegSug, setShowVehRegSug] = useState(false);
  const [filters, setFilters] = useState(defaultFilters);
  const [gvListItems, setGvListItems] = useState([]);
  const [gvMonthly, setGvMonthly] = useState([]);
  const [gvDieselHr, setGvDieselHr] = useState([]);
  const [gvDieselKm, setGvDieselKm] = useState([]);
  const [gvVehicleWise, setGvVehicleWise] = useState([]);

  const fieldClass = useMemo(
    () =>
      `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () => `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const tdClass = useMemo(
    () => `px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`,
    [isDark]
  );

  const readPassedContext = useCallback(() => {
    const ctx = {
      logvehicleid: toStr(searchParams.get("logvehicleid")),
      logvehiclecode: toStr(searchParams.get("logvehiclecode")),
      logvehicledetid: toStr(searchParams.get("logvehicledetid")),
      logvehiclebranchid: toStr(searchParams.get("logvehiclebranchid")),
      logvehicleregno: toStr(searchParams.get("logvehicleregno")),
    };
    const anyPassed =
      ctx.logvehicleid || ctx.logvehiclecode || ctx.logvehicledetid || ctx.logvehiclebranchid || ctx.logvehicleregno;
    return anyPassed ? ctx : null;
  }, [searchParams]);

  const buildParams = useCallback(
    (f = filters, ctx = logCtx) => {
      const p = {
        vehtype: toStr(f.vehtype || "O"),
        branch: session.branchType === "HO" ? toStr(f.branch) : String(session.branchId),
        vehRegNo: toStr(f.vehRegNo),
        month: toStr(f.month),
        year: toStr(f.year),
        fromDate: toStr(f.fromDate),
        toDate: toStr(f.toDate),
        vehicleMainType: toStr(f.vehicleMainType),
        vehicleName: toStr(f.vehicleName),
        vehicleCode: toStr(f.vehicleCode),
        logvehicleid: toStr(ctx.logvehicleid),
        logvehiclecode: toStr(ctx.logvehiclecode),
        logvehicledetid: toStr(ctx.logvehicledetid),
        logvehiclebranchid: toStr(ctx.logvehiclebranchid),
        logvehicleregno: toStr(ctx.logvehicleregno),
      };
      Object.keys(p).forEach((k) => {
        if (p[k] === undefined || p[k] === null) p[k] = "";
      });
      return p;
    },
    [filters, logCtx, session.branchId, session.branchType]
  );

  const loadInit = useCallback(async () => {
    try {
      setDropdownLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchType: session.branchType,
          branchId: session.branchId,
          loginUserId: session.loginUserId,
        },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");
      setBranches(toList(data.branches).length ? data.branches : [{ label: "Select", value: "Select" }]);
      setYears(toList(data.years).length ? data.years : [{ label: "Select", value: "Select" }]);
      setVehicleMainTypes(toList(data.vehicleMainTypes).length ? data.vehicleMainTypes : [{ label: "Select", value: "Select" }]);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Failed to load dropdowns");
      setBranches([{ label: "Select", value: "Select" }]);
      setYears([{ label: "Select", value: "Select" }]);
      setVehicleMainTypes([{ label: "Select", value: "Select" }]);
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchType, session.branchId, session.loginUserId, commonHeaders]);

  const clearAllGrids = useCallback(() => {
    setGvListItems([]);
    setGvMonthly([]);
    setGvDieselHr([]);
    setGvDieselKm([]);
    setGvVehicleWise([]);
  }, []);

  const loadAllGrids = useCallback(
    async (nextFilters = filters, nextCtx = logCtx) => {
      try {
        setListLoading(true);
        const params = buildParams(nextFilters, nextCtx);
        const [a, b, c, d, e] = await Promise.all([
          axios.get(`${API_BASE}/list`, { params, headers: commonHeaders }),
          axios.get(`${API_BASE}/monthlyIssue`, { params, headers: commonHeaders }),
          axios.get(`${API_BASE}/hrConsumption`, { params, headers: commonHeaders }),
          axios.get(`${API_BASE}/kmConsumption`, { params, headers: commonHeaders }),
          axios.get(`${API_BASE}/vehicleWiseIssue`, { params, headers: commonHeaders }),
        ]);
        const pack = [a.data, b.data, c.data, d.data, e.data];
        const bad = pack.find((x) => !x?.success);
        if (bad) throw new Error(bad?.message || "Load failed");
        setGvListItems(toList(a.data.rows));
        setGvMonthly(toList(b.data.rows));
        setGvDieselHr(toList(c.data.rows));
        setGvDieselKm(toList(d.data.rows));
        setGvVehicleWise(toList(e.data.rows));
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        clearAllGrids();
      } finally {
        setListLoading(false);
      }
    },
    [filters, logCtx, buildParams, commonHeaders, clearAllGrids]
  );

  const fetchVehRegSuggestions = useCallback(
    async (prefix, nextBranch, nextVehType) => {
      try {
        const p = toStr(prefix);
        if (!p) {
          setVehRegSuggestions([]);
          return;
        }
        const { data } = await axios.get(`${API_BASE}/vehRegAuto`, {
          params: {
            prefix: p,
            branchId: session.branchType === "HO" ? toStr(nextBranch || filters.branch) : String(session.branchId),
            vehtype: toStr(nextVehType || filters.vehtype || "O"),
            logVehicleId: "0",
          },
          headers: commonHeaders,
        });
        setVehRegSuggestions(data?.success ? toList(data.rows) : []);
      } catch {
        setVehRegSuggestions([]);
      }
    },
    [filters.branch, filters.vehtype, session.branchType, session.branchId, commonHeaders]
  );

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      const ctx = readPassedContext();
      const baseFilters = createDefaultFilters(session.branchType, session.branchId);
      if (ctx) {
        setLogCtx(ctx);
        const nextFilters = {
          ...baseFilters,
          branch: ctx.logvehiclebranchid || baseFilters.branch,
          vehRegNo: ctx.logvehicleregno || "",
          vehicleCode: ctx.logvehiclecode || "",
        };
        setFilters(nextFilters);
        clearAllGrids();
        await loadInit();
        await loadAllGrids(nextFilters, ctx);
      } else {
        const emptyCtx = {
          logvehicleid: "",
          logvehiclecode: "",
          logvehicledetid: "",
          logvehiclebranchid: "",
          logvehicleregno: "",
        };
        setLogCtx(emptyCtx);
        setFilters(baseFilters);
        clearAllGrids();
        await loadInit();
        await loadAllGrids(baseFilters, emptyCtx);
      }
    } finally {
      setPageLoading(false);
    }
  }, [readPassedContext, session.branchType, session.branchId, clearAllGrids, loadInit, loadAllGrids]);

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setField(name, value);
      if (name === "vehRegNo") {
        setShowVehRegSug(true);
        fetchVehRegSuggestions(value, filters.branch, filters.vehtype);
      }
      if (name === "branch") setVehRegSuggestions([]);
      if (name === "vehtype" && filters.vehRegNo) {
        fetchVehRegSuggestions(filters.vehRegNo, filters.branch, value);
      }
    },
    [setField, fetchVehRegSuggestions, filters.branch, filters.vehtype, filters.vehRegNo]
  );

  const selectVehReg = useCallback((val) => {
    setFilters((prev) => ({ ...prev, vehRegNo: val }));
    setShowVehRegSug(false);
  }, []);

  const handleSearch = useCallback(async () => {
    if (isSelect(filters.month) || isSelect(filters.year)) {
      toast.error("Please select Month and Year");
      return;
    }
    await loadAllGrids(filters, logCtx);
    toast.success("Search completed");
  }, [filters, logCtx, loadAllGrids]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    const merged = {
      ...reset,
      branch: session.branchType === "HO" ? "Select" : String(session.branchId),
      vehicleCode: logCtx.logvehiclecode || "",
      vehRegNo: logCtx.logvehicleregno || "",
    };
    setFilters(merged);
    await loadAllGrids(merged, logCtx);
    toast.success("View all loaded");
  }, [session.branchType, session.branchId, logCtx, loadAllGrids]);

  const handleReset = useCallback(() => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters({
      ...reset,
      vehicleCode: logCtx.logvehiclecode || "",
      vehRegNo: logCtx.logvehicleregno || "",
      branch: logCtx.logvehiclebranchid || (session.branchType === "HO" ? "Select" : String(session.branchId)),
    });
    clearAllGrids();
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchType, session.branchId, logCtx, clearAllGrids]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const openPrintPage = useCallback(
    (path, extra = {}) => {
      const q = new URLSearchParams({ ...buildParams(filters, logCtx), ...extra });
      window.open(`${path}?${q.toString()}`, "_blank", "noopener,noreferrer");
    },
    [buildParams, filters, logCtx]
  );

  const printMonthlyDieselConsumption = useCallback(() => openPrintPage("/vehicle/VehicleWiseReportPrint"), [openPrintPage]);
  const printMonthlyWiseDieselIssue = useCallback(() => openPrintPage("/vehicle/MonthlyDieselReportPrint"), [openPrintPage]);
  const printDieselConsumption = useCallback((type) => openPrintPage("/vehicle/DieselConsumptionReport_Print", { type }), [openPrintPage]);
  const printVehicleWiseDieselIssue = useCallback(() => openPrintPage("/vehicle/VehicleWiseDieselIssueReportPrint"), [openPrintPage]);

  const handleExportGrid1 = useCallback(() => {
    if (!gvListItems.length) return toast.info("No rows to export");
    exportToCsv(gvListItems, "monthly-diesel-consumption.csv");
    toast.success("Exported Grid1");
  }, [gvListItems]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  const vehicleWiseTotals = useMemo(() => {
    const rows = gvVehicleWise || [];
    const sumWorkingHRS = rows.reduce((a, r) => a + toNum(r?.workingHRS), 0);
    const sumWorkingKM = rows.reduce((a, r) => a + toNum(r?.workingkm), 0);
    const sumDieselIssue = rows.reduce((a, r) => a + toNum(r?.diselissue), 0);
    const n = rows.length || 1;
    return {
      avgWorkingHRS: sumWorkingHRS / n,
      avgWorkingKM: sumWorkingKM / n,
      avgDieselIssue: sumDieselIssue / n,
    };
  }, [gvVehicleWise]);

  const hasPassedCtx =
    logCtx.logvehicleid || logCtx.logvehiclecode || logCtx.logvehicledetid || logCtx.logvehiclebranchid || logCtx.logvehicleregno;

  const statusText = pageLoading
    ? "Loading page..."
    : dropdownLoading
    ? "Loading dropdowns..."
    : listLoading
    ? "Searching..."
    : "Ready";

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">DIESEL CONSUMPTION REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>
                Diesel Issue & Consumption Details
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} • User ID: {session.loginUserId || "-"}
              </p>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handleExportGrid1} disabled={isBusy || gvListItems.length === 0} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy || gvListItems.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaFileExport /> Export
              </button>
              <button onClick={handleRefresh} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSyncAlt /> Refresh
              </button>
              <button onClick={handleBack} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaArrowLeft /> Back
              </button>
              <button onClick={() => setShowFilters((p) => !p)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {hasPassedCtx && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>Passed Vehicle Context</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0"><label className={labelClass}>Vehicle ID</label><input className={fieldClass} value={logCtx.logvehicleid} readOnly /></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Code</label><input className={fieldClass} value={logCtx.logvehiclecode} readOnly /></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Det ID</label><input className={fieldClass} value={logCtx.logvehicledetid} readOnly /></div>
                <div className="min-w-0"><label className={labelClass}>Branch ID</label><input className={fieldClass} value={logCtx.logvehiclebranchid} readOnly /></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Reg No</label><input className={fieldClass} value={logCtx.logvehicleregno} readOnly /></div>
              </div>
            </div>
          )}

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleReset} type="button" disabled={isBusy} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>Clear</button>
                  <button onClick={handleViewAll} type="button" disabled={isBusy} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} type="button" disabled={isBusy} className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${isBusy ? "opacity-60 cursor-not-allowed" : ""} ${buttonClass} ${hoverClass} text-white`}><FaSearch /> {listLoading ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex gap-6 flex-wrap">
                  <label className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                    <input type="radio" name="vehtype" value="O" checked={filters.vehtype === "O"} onChange={handleChange} disabled={isBusy} />
                    Own Vehicle
                  </label>
                  <label className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                    <input type="radio" name="vehtype" value="H" checked={filters.vehtype === "H"} onChange={handleChange} disabled={isBusy} />
                    Hire Vehicle
                  </label>
                </div>
                <div className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  <FaInfoCircle /> Month + Year are mandatory.
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select className={fieldClass} name="branch" value={filters.branch} onChange={handleChange} disabled={isBusy || session.branchType !== "HO"}>
                    {branches.map((b, i) => <option key={`${b.value}-${i}`} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
                <div className="min-w-0 relative">
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input className={fieldClass} name="vehRegNo" value={filters.vehRegNo} onChange={handleChange} onBlur={() => setTimeout(() => setShowVehRegSug(false), 150)} onFocus={() => setShowVehRegSug(true)} placeholder="Type reg no..." disabled={isBusy} />
                  {showVehRegSug && (vehRegSuggestions?.length || 0) > 0 && (
                    <div className={`absolute z-20 mt-1 w-full rounded-lg border max-h-56 overflow-auto ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
                      {vehRegSuggestions.map((s, idx) => {
                        const label = toStr(s?.label ?? s?.value ?? s);
                        return (
                          <button key={idx} type="button" className={`w-full text-left px-3 py-2 text-sm ${isDark ? "text-gray-200 hover:bg-gray-800" : "text-gray-800 hover:bg-gray-100"}`} onClick={() => selectVehReg(label)}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Month <span className="text-red-500">*</span></label>
                  <select className={fieldClass} name="month" value={filters.month} onChange={handleChange} disabled={isBusy}>
                    <option value="Select">Select</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Year <span className="text-red-500">*</span></label>
                  <select className={fieldClass} name="year" value={filters.year} onChange={handleChange} disabled={isBusy}>
                    {years.map((y, i) => <option key={`${y.value}-${i}`} value={y.value}>{y.label}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>From Date</label>
                  <input className={fieldClass} name="fromDate" value={filters.fromDate} onChange={handleChange} placeholder="dd/MM/yyyy" disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>To Date</label>
                  <input className={fieldClass} name="toDate" value={filters.toDate} onChange={handleChange} placeholder="dd/MM/yyyy" disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Type</label>
                  <select className={fieldClass} name="reportType" value={filters.reportType} onChange={handleChange} disabled={isBusy}>
                    <option value="Select">Select</option>
                    <option value="Vehiclewise Diesel Report">Vehiclewise Diesel Report</option>
                    <option value="Monthly Diesel Issue">Monthly Diesel Issue</option>
                    <option value="Diesel Consumption">Diesel Consumption</option>
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select className={fieldClass} name="vehicleMainType" value={filters.vehicleMainType} onChange={handleChange} disabled={isBusy}>
                    {vehicleMainTypes.map((t, i) => <option key={`${t.value}-${i}`} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Name</label>
                  <input className={fieldClass} name="vehicleName" value={filters.vehicleName} onChange={handleChange} disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <input className={fieldClass} name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} disabled={isBusy} />
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
              G1:{gvListItems.length} • G2:{gvMonthly.length} • G3:{gvDieselHr.length} • G4:{gvDieselKm.length} • G5:{gvVehicleWise.length}
            </div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{statusText}</div>
          </div>

          <SectionHeader title="Monthly Diesel Consumption Report" onPrint={printMonthlyDieselConsumption} isBusy={isBusy} buttonClass={buttonClass} hoverClass={hoverClass} isDark={isDark} />
          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg mb-6">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle Reg No</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Monthly Diesel</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Cons/Hr</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Cons/KM</th>
                </tr>
              </thead>
              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {gvListItems.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center">No Records Found!</td></tr>
                ) : gvListItems.map((r, idx) => (
                  <tr key={idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                    <td className={tdClass}>{idx + 1}</td>
                    <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>{toStr(r?.fld_vehicle_regno) || "-"}</td>
                    <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{toStr(r?.fld_vehicle_name) || "-"}</td>
                    <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>{toStr(r?.disqty) || "-"}</td>
                    <td className={tdClass}>{toStr(r?.disconsumptionhr) || "-"}</td>
                    <td className={tdClass}>{toStr(r?.disconsumptionkm) || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SectionHeader title="Monthlywise Diesel Issue Details" onPrint={printMonthlyWiseDieselIssue} isBusy={isBusy} buttonClass={buttonClass} hoverClass={hoverClass} isDark={isDark} />
          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg mb-6">
            <AutoGrid rows={gvMonthly} isDark={isDark} tableHeaderClass={tableHeaderClass} />
          </div>

          <SectionHeader title="Diesel Consumption For Hr Basis Vehicle" onPrint={() => printDieselConsumption("hr")} isBusy={isBusy} buttonClass={buttonClass} hoverClass={hoverClass} isDark={isDark} />
          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg mb-6">
            <AutoGrid rows={gvDieselHr} isDark={isDark} tableHeaderClass={tableHeaderClass} />
          </div>

          <SectionHeader title="Diesel Consumption For KM Basis Vehicle" onPrint={() => printDieselConsumption("km")} isBusy={isBusy} buttonClass={buttonClass} hoverClass={hoverClass} isDark={isDark} />
          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg mb-6">
            <AutoGrid rows={gvDieselKm} isDark={isDark} tableHeaderClass={tableHeaderClass} />
          </div>

          <SectionHeader title="VehicleWise Diesel Issue Report" onPrint={printVehicleWiseDieselIssue} isBusy={isBusy} buttonClass={buttonClass} hoverClass={hoverClass} isDark={isDark} />
          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg mb-6">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Month Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Diesel Issue</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Cons HR</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Cons KM</th>
                </tr>
              </thead>
              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {gvVehicleWise.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center">No Records Found!</td></tr>
                ) : gvVehicleWise.map((r, idx) => (
                  <tr key={idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                    <td className={tdClass}>{idx + 1}</td>
                    <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{toStr(r?.monthname) || "-"}</td>
                    <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>{toStr(r?.diselissue) || "-"}</td>
                    <td className={tdClass}>{toStr(r?.fld_diselconsumptionhr) || "-"}</td>
                    <td className={tdClass}>{toStr(r?.fld_diselconsumptionkm) || "-"}</td>
                  </tr>
                ))}
              </tbody>
              {gvVehicleWise.length > 0 && (
                <tfoot>
                  <tr className={isDark ? "bg-gray-700 text-gray-100" : "bg-gray-100 text-gray-800"}>
                    <td className="px-4 py-3 font-semibold" colSpan={2}>Average Total</td>
                    <td className="px-4 py-3 font-semibold">{fmt2(vehicleWiseTotals.avgDieselIssue)}</td>
                    <td className="px-4 py-3" colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <ToastContainer position="top-right" autoClose={2500} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}

function SectionHeader({ title, onPrint, isBusy, buttonClass, hoverClass, isDark }) {
  return (
    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
      <div className={`text-base font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{title}</div>
      <button onClick={onPrint} disabled={isBusy} className={`px-3 py-2 rounded-lg text-white ${buttonClass} ${hoverClass} flex items-center gap-2 ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`} type="button">
        <FaPrint /> Print
      </button>
    </div>
  );
}

function AutoGrid({ rows, isDark, tableHeaderClass }) {
  const cols = useMemo(() => {
    if (!rows?.length) return [];
    return Object.keys(rows[0] || {}).filter((c) => !/id$/i.test(c)).slice(0, 6);
  }, [rows]);

  if (!rows?.length) {
    return <div className={`p-8 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>No Records Found!</div>;
  }

  return (
    <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
      <thead className={`${tableHeaderClass} text-white`}>
        <tr>
          <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
          {cols.map((c) => (
            <th key={c} className="px-4 py-3 text-left text-sm font-semibold">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
        {rows.map((r, idx) => (
          <tr key={idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
            <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{idx + 1}</td>
            {cols.map((c) => (
              <td key={c} className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{String(r?.[c] ?? "")}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}